import {
	CheckCircleIcon,
	ChevronRightIcon,
	EyeIcon,
	InformationCircleIcon,
	XCircleIcon,
} from '@heroicons/react/24/outline'
import { yupResolver } from '@hookform/resolvers/yup'
import { ColumnDef } from '@tanstack/react-table'
import { ArrowDown, ArrowUp, Check, ChevronDownIcon, ChevronsUpDown, Loader2 } from 'lucide-react'
import React, { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'

import { format } from 'date-fns'
import { toast } from 'sonner'
import { useMediaQuery } from 'usehooks-ts'
import * as yup from 'yup'

import { ArchiveIcon, TaskSquareIcon } from '@/assets/icons'
import { Button } from '@/components/ui/button'
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from '@/components/ui/command'
import { DataTable, DataTableColumnHeader } from '@/components/ui/data-table'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import MultipleSelector from '@/components/ui/multi-selector'
import { PaginationComponent } from '@/components/ui/pagination'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
	ResponsiveModal,
	ResponsiveModalContent,
	ResponsiveModalDescription,
	ResponsiveModalHeader,
	ResponsiveModalTitle,
} from '@/components/ui/responsive-dialog'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { VirtualizedCombobox } from '@/components/ui/virtualized-combobox'
import { capitalizeFirstLetter } from '@/lib/helper'
import { cn } from '@/lib/utils'
import bookingService from '@/services/booking-service'
import standService from '@/services/stand-service'
import userService from '@/services/user-service'

export const StandsTable = () => {
	const queryClient = useQueryClient()
	const navigate = useNavigate()

	const searchableColumns = [
		{ label: 'Regal Nummer', value: 'id' },
		{ label: 'Status', value: 'status' },
	]

	const isSmallScreen = useMediaQuery('(max-width: 64rem)')

	const [expandedRows, setExpandedRows] = useState<number[]>([])
	const [filterValue, setFilterValue] = useState('')
	const [isStandArchiveDialogOpen, setIsStandArchiveDialogOpen] = useState(false)
	const [isUserArchiveDialogOpen, setIsUserArchiveDialogOpen] = useState(false)
	const [isBookingsDialogOpen, setIsBookingsDialogOpen] = useState(false)
	const [page, setPage] = useState(1)
	const [selectedColumn, setSelectedColumn] = useState('id')
	const [rowsPerPage, setRowsPerPage] = useState(50)
	const [sortColumn, setSortColumn] = useState('')
	const [sortDirection, setSortDirection] = useState('')
	const [open, setOpen] = useState(true)

	const { data: bookings, status } = useQuery(
		['fetch-all-bookings', isBookingsDialogOpen],
		() =>
			bookingService.fetchBookingsData({
				pageNumber: '1',
				pageSize: '999999999999999',
			}),
		{
			enabled: isBookingsDialogOpen,
			refetchOnWindowFocus: false,
			cacheTime: 1000 * 60 * 2,
			onError: () => {
				toast.error('Error fetching bookings')
			},
		},
	)

	const schemaByStand = yup.object<{ standId: string; userId: string }>().shape({
		standId: yup.string().required('Stand ist erforderlich!'),
		userId: yup.string().required('Benutzer ist erforderlich!'),
	})

	const schemaByBooking = yup.object<{ bookingId: string }>().shape({
		bookingId: yup.string().required('Buchungs-ID ist erforderlich!'),
	})

	const schemaByUser = yup
		.object<{ standId: { value: string; label: string }[]; userId: string }>()
		.shape({
			standId: yup
				.array()
				.transform((value, originalValue) => {
					return Array.isArray(originalValue) && originalValue.length === 0 ? undefined : value
				})
				.of(
					yup.object({
						label: yup.string().required(),
						value: yup.string().required(),
					}),
				)
				.default([])
				.required('Stand ist erforderlich!'),
			userId: yup.string().required('Benutzer ist erforderlich!'),
		})

	const formByStand = useForm<{ standId: string; userId: string }>({
		resolver: yupResolver(schemaByStand),
		defaultValues: { userId: '', standId: '' },
		mode: 'all',
	})

	const formByUser = useForm<{ standId: { value: string; label: string }[]; userId: string }>({
		resolver: yupResolver(schemaByUser),
		defaultValues: { userId: '', standId: [] },
		mode: 'all',
	})

	const formByBookings = useForm<{ bookingId: string }>({
		resolver: yupResolver(schemaByBooking),
		defaultValues: { bookingId: '' },
		mode: 'all',
	})

	const { data, refetch, isLoading } = useQuery(
		'fetch-stands',
		() =>
			standService.fetchStandsData({
				pageNumber: page.toString(),
				pageSize: rowsPerPage.toString(),
				filterColumn: selectedColumn,
				filterValue,
			}),
		{
			refetchOnWindowFocus: false,
			onError: () => {
				toast.error('Error fetching stands')
			},
		},
	)

	const { data: allStands } = useQuery(
		['fetch-stands', isStandArchiveDialogOpen],
		() =>
			standService.fetchStandsData({
				pageNumber: page.toString(),
				pageSize: '9999999',
				filterColumn: selectedColumn,
				filterValue,
			}),
		{
			enabled: isStandArchiveDialogOpen,
			refetchOnWindowFocus: false,
			onError: () => {
				toast.error('Error fetching stands')
			},
		},
	)

	const { data: usersList } = useQuery('fetch-users', () => userService.fetchAllUsers(), {
		refetchOnWindowFocus: false,
		enabled: isUserArchiveDialogOpen,
		onError: () => {
			toast.error('Error fetching users')
		},
	})

	const { mutate: archiveProduct, isLoading: isArchiving } = useMutation({
		mutationKey: 'archive-product',
		mutationFn: (data: { standId: string[]; userId: string }) => standService.archiveProduct(data),
	})

	const { mutate: archiveProductByBooking, isLoading: isArchivingByBooking } = useMutation({
		mutationKey: 'archive-product-by-booking',
		mutationFn: (data: { bookingId: string }) => standService.archiveProductByBookingId(data),
	})

	const { mutate: fetchUsers, data: users } = useMutation({
		mutationKey: 'fetch-users-for-stand',
		mutationFn: (standId: Stand['id']) => standService.fetchUsersForSelectedStand(standId),
	})

	const { mutate: fetchStands, data: stands } = useMutation({
		mutationKey: 'fetch-stands-for-users',
		mutationFn: (userId: User['id']) => standService.fetchStandsForSelectedUser(userId),
	})

	const { mutate: updateStand } = useMutation({
		mutationKey: 'update-stand',
		mutationFn: ({ id, status }: { id: number; status: 'Disabled' | 'active' }) =>
			standService.updateStand(id, status),
	})

	useEffect(() => {
		refetch()
	}, [rowsPerPage, page])

	const sortedData = useMemo(() => {
		if (!data) return []
		if (!sortColumn) return data.data

		return [...data.data].sort((a, b) => {
			const aValue = a[sortColumn as keyof Stand]
			const bValue = b[sortColumn as keyof Stand]

			if (aValue == null || bValue == null) return 0

			let aVal: any = aValue
			let bVal: any = bValue

			if (aVal instanceof Date) aVal = aVal.getTime()
			if (bVal instanceof Date) bVal = bVal.getTime()

			const aParsed = typeof aVal === 'string' ? Date.parse(aVal) : NaN
			const bParsed = typeof bVal === 'string' ? Date.parse(bVal) : NaN

			if (!isNaN(aParsed) && !isNaN(bParsed)) {
				aVal = aParsed
				bVal = bParsed
			}

			if (typeof aVal === 'number' && typeof bVal === 'number') {
				return sortDirection === 'asc' ? aVal - bVal : bVal - aVal
			}

			if (typeof aVal === 'string' && typeof bVal === 'string') {
				return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
			}

			return 0
		})
	}, [data, sortColumn, sortDirection])

	const filteredUsers = (Array.isArray(usersList) ? usersList : [])?.filter(user =>
		users?.includes(user.id),
	)

	const toggleRow = (id: number) => {
		setExpandedRows(prev =>
			prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id],
		)
	}

	const handleSort = (column: string) => {
		if (sortColumn === column) {
			setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'))
		} else {
			setSortColumn(column)
			setSortDirection('asc')
		}
	}

	const handleStandUpdate = (id: number, status: 'Disabled' | 'active') => {
		updateStand(
			{ id, status },
			{
				onSuccess: () => {
					toast.success('Stand updated successfully!')
					refetch()
				},
				onError: (err: any) => {
					toast.error(err?.response?.data?.message ?? 'Failed to update stand.Please try again')
				},
			},
		)
	}

	const onStandSubmit = (data: { standId: string; userId: string }) => {
		const payload = {
			standId: [data.standId],
			userId: data.userId,
		}
		archiveProduct(payload, {
			onSuccess: () => {
				toast.success('Products archived successfully')
				formByStand.reset()
				setIsStandArchiveDialogOpen(false)
				queryClient.invalidateQueries({ queryKey: ['fetch-users-for-stand'] })
			},
			onError: () => {
				toast.error('Failed to archive products. Please try again')
			},
		})
	}

	const onBookingSubmit = (data: { bookingId: string }) => {
		archiveProductByBooking(data, {
			onSuccess: () => {
				toast.success('Products archived successfully')
				formByBookings.reset()
				setIsBookingsDialogOpen(false)
				queryClient.invalidateQueries({ queryKey: ['fetch-users-for-stand'] })
			},
			onError: () => {
				toast.error('Failed to archive products. Please try again')
			},
		})
	}

	const onUserSubmit = (data: { standId: { value: string; label: string }[]; userId: string }) => {
		const payload = {
			standId: data.standId.map(stand => stand.value),
			userId: data.userId,
		}
		archiveProduct(payload, {
			onSuccess: () => {
				toast.success('Products archived successfully')
				formByUser.reset()
				setIsUserArchiveDialogOpen(false)
				queryClient.invalidateQueries({ queryKey: ['fetch-users-for-stand'] })
			},
			onError: () => {
				toast.error('Failed to archive products. Please try again')
			},
		})
	}

	const columns: ColumnDef<Stand>[] = [
		{
			accessorKey: 'label',
			header: ({ column }) => <DataTableColumnHeader column={column} title="Regal Nummer" />,
		},
		{
			accessorKey: 'is_available',
			accessorFn: (row: Stand) => (row.is_available ? 'Verfügbar' : 'Gebucht'),
			header: ({ column }) => <DataTableColumnHeader column={column} title="Verfügbarkeit" />,
			cell: ({ row }) => (
				<div
					className={cn(
						'w-fit rounded-[18px] p-1.5 px-3',
						row.original.is_available
							? 'bg-[#61B9641A] text-[#61B964]'
							: 'bg-[#FFEFF2] text-[#DB5358]',
					)}>
					{row.getValue('is_available')}
				</div>
			),
		},
		{
			accessorKey: 'status',
			accessorFn: (row: Stand) => (row.status === 'active' ? 'Aktive' : 'Deaktiviert'),
			header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
			cell: ({ row }) => (
				<div
					className={cn(
						'w-fit rounded-[18px] p-1.5 px-3',
						row.original.status === 'active'
							? 'bg-[#61B9641A] text-[#61B964]'
							: 'bg-[#FFEFF2] text-[#DB5358]',
					)}>
					{row.getValue('status')}
				</div>
			),
		},
		{
			accessorKey: 'createdAt',
			header: ({ column }) => <DataTableColumnHeader column={column} title="Datum" />,
			cell: ({ row }) => <div>{format(new Date(row.getValue('createdAt')), 'dd-MM-yyyy')}</div>,
		},
		{
			id: 'action-stands',
			enableSorting: false,
			header: ({ column }) => <DataTableColumnHeader column={column} title="Aktionen" />,
			cell: ({ row }) => (
				<div className="flex items-center justify-center gap-0.5">
					<Tooltip>
						<TooltipTrigger
							onClick={() => navigate(`/stand-details/${row.original.id}`)}
							className="border cursor-pointer border-[#0B224D33] hover:border-secondary size-[33px] flex items-center justify-center rounded-full">
							<EyeIcon className="shrink-0 size-4" />
						</TooltipTrigger>
						<TooltipContent side="bottom">
							<p className="text-xs font-medium">Sicht</p>
						</TooltipContent>
					</Tooltip>
					<Tooltip>
						<TooltipTrigger
							onClick={() =>
								handleStandUpdate(
									row.original.id,
									row.original.status === 'active' ? 'Disabled' : 'active',
								)
							}
							className="border cursor-pointer border-[#0B224D33] hover:border-secondary size-[33px] flex items-center justify-center rounded-full">
							{row.original.status === 'active' ? (
								<XCircleIcon className="shrink-0 size-4" />
							) : (
								<CheckCircleIcon className="shrink-0 size-4" />
							)}
						</TooltipTrigger>
						<TooltipContent side="bottom">
							<p className="text-xs font-medium">
								{row.original.status === 'active' ? 'Deaktiviert' : 'Aktivieren'}
							</p>
						</TooltipContent>
					</Tooltip>
				</div>
			),
		},
	]

	const MobileStandsTable = () => {
		return (
			<Table>
				<TableHeader>
					<TableRow className="bg-[#F7F8F880] border-b-0! py-[13px] px-4 rounded-lg">
						<TableHead
							align="center"
							onClick={() => handleSort('label')}
							className="cursor-pointer text-center text-muted-foreground text-[10px] font-medium">
							<div className="flex items-center gap-1 justify-center">
								Regal Nummer
								{sortColumn === 'label' ? (
									sortDirection === 'desc' ? (
										<ArrowDown size={12} />
									) : (
										<ArrowUp size={12} />
									)
								) : (
									<ChevronsUpDown size={12} />
								)}
							</div>
						</TableHead>
						<TableHead
							align="center"
							onClick={() => handleSort('status')}
							className="cursor-pointer text-center text-muted-foreground text-[10px] font-medium">
							<div className="flex items-center gap-1 justify-center">
								Status
								{sortColumn === 'status' ? (
									sortDirection === 'desc' ? (
										<ArrowDown size={12} />
									) : (
										<ArrowUp size={12} />
									)
								) : (
									<ChevronsUpDown size={12} />
								)}
							</div>
						</TableHead>
						<TableHead
							align="center"
							onClick={() => handleSort('createdAt')}
							className="cursor-pointer text-center text-muted-foreground text-[10px] font-medium">
							<div className="flex items-center gap-1 justify-center">
								Datum
								{sortColumn === 'createdAt' ? (
									sortDirection === 'desc' ? (
										<ArrowDown size={12} />
									) : (
										<ArrowUp size={12} />
									)
								) : (
									<ChevronsUpDown size={12} />
								)}
							</div>
						</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{sortedData?.map((stand, index) => (
						<React.Fragment key={stand.id.toString() + index.toString()}>
							<TableRow
								className={cn(
									'h-14',
									expandedRows.includes(stand.id) ? '!border-b-0' : 'border-b border-[#F4F4F4]',
								)}
								onClick={() => toggleRow(stand.id)}>
								<TableCell
									align="center"
									className={cn('text-xs', {
										'text-muted-foreground': !expandedRows.includes(stand.id),
									})}>
									<div className="flex items-center">
										<ChevronRightIcon
											className={cn(
												'size-3 shrink-0 -mt-0.5',
												expandedRows.includes(stand.id)
													? 'rotate-90 text-secondary'
													: 'text-muted-foreground',
											)}
										/>
										{stand.label}
									</div>
								</TableCell>
								<TableCell
									align="center"
									className={cn('text-[10px]', {
										'text-muted-foreground': !expandedRows.includes(stand.id),
									})}>
									<div
										className={cn(
											'text-[10px] w-fit p-1.5 px-2 rounded-[18px]',
											stand.status === 'active'
												? 'bg-[#61B9641A] text-[#61B964]'
												: 'bg-[#FFEFF2] text-[#DB5358]',
										)}>
										{stand.status === 'active' ? 'Aktiv' : 'Deaktiviert'}
									</div>
								</TableCell>
								<TableCell
									align="center"
									className={cn('text-xs', {
										'text-muted-foreground': !expandedRows.includes(stand.id),
									})}>
									{format(new Date(stand.createdAt), 'dd-MM-yyyy')}
								</TableCell>
							</TableRow>
							{expandedRows.includes(stand.id) && (
								<TableRow
									className={cn(
										'mt-2',
										!expandedRows.includes(stand.id) ? '!border-b-0' : 'border-b border-[#F4F4F4]',
									)}>
									<TableCell className="pt-0" colSpan={3}>
										<div className="px-3 flex flex-col gap-4">
											<div className="flex items-center justify-between">
												<h6 className="text-muted-foreground text-[10px] font-medium">
													Verfügbarkeit
												</h6>
												<div
													className={cn(
														'text-[10px] w-fit p-1.5 px-2 rounded-[18px]',
														stand.is_available
															? 'bg-[#61B9641A] text-[#61B964]'
															: 'bg-[#FFEFF2] text-[#DB5358]',
													)}>
													{stand.is_available ? 'Verfügbar' : 'Gebucht'}
												</div>
											</div>

											<div className="flex items-center justify-between">
												<div className="flex items-center gap-1">
													<InformationCircleIcon className="text-muted-foreground size-3" />
													<h6 className="text-muted-foreground text-[10px] font-medium">Aktion</h6>
												</div>
												<div className="flex items-center gap-1">
													<div
														onClick={() => navigate(`/stand-details/${stand.id}`)}
														className="border cursor-pointer border-[#0B224D33] hover:border-secondary size-6 flex items-center justify-center rounded-full">
														<EyeIcon className="shrink-0 max-lg:size-3" />
													</div>
													<div
														onClick={() =>
															handleStandUpdate(
																stand.id,
																stand.status === 'active' ? 'Disabled' : 'active',
															)
														}
														className="border cursor-pointer border-[#0B224D33] hover:border-secondary size-6 flex items-center justify-center rounded-full">
														{stand.status === 'active' ? (
															<XCircleIcon className="shrink-0 max-lg:size-3" />
														) : (
															<CheckCircleIcon className="shrink-0 max-lg:size-3" />
														)}
													</div>
												</div>
											</div>
										</div>
									</TableCell>
								</TableRow>
							)}
						</React.Fragment>
					))}
				</TableBody>
			</Table>
		)
	}

	return (
		<div className="rounded-[20px] bg-white max-lg:p-4 max-lg:pr-3 lg:px-4 lg:py-6 flex flex-col">
			<ResponsiveModal
				open={isStandArchiveDialogOpen}
				onOpenChange={() => {
					setIsStandArchiveDialogOpen(false)
					formByStand.reset()
					queryClient.invalidateQueries({ queryKey: ['fetch-users-for-stand'] })
				}}>
				<ResponsiveModalContent
					onPointerDownOutside={e => open && e.preventDefault()}
					className="gap-0 !overflow-visible">
					<ResponsiveModalHeader className="max-lg:pb-2">
						<ResponsiveModalTitle className="mb-0 max-lg:text-left">
							Produkte Archivieren von Stand
						</ResponsiveModalTitle>
						<ResponsiveModalDescription className="sr-only">
							Produkte Archivieren von Stand
						</ResponsiveModalDescription>
					</ResponsiveModalHeader>
					<Form {...formByStand}>
						<form
							onSubmit={formByStand.handleSubmit(onStandSubmit)}
							className="mt-4 lg:mt-8 flex flex-col gap-6 lg:gap-8">
							<FormField
								control={formByStand.control}
								name="standId"
								render={({ field }) => (
									<FormItem className="w-full">
										<FormLabel className="text-muted-foreground">
											Bitte wählen Sie das Regal.{' '}
											<span className="text-destructive-foreground">*</span>
										</FormLabel>
										<Select
											onValueChange={value => {
												fetchUsers(Number(value))
												field.onChange(value)
												formByStand.setValue('userId', '')
											}}
											defaultValue={field.value}>
											<FormControl autoFocus>
												<SelectTrigger>
													<SelectValue placeholder="Regal." />
												</SelectTrigger>
											</FormControl>
											<SelectContent>
												{allStands &&
													allStands.data.length > 0 &&
													allStands.data.map(stand => (
														<SelectItem key={'stand_' + stand.id} value={stand.id.toString()}>
															{stand.label}
														</SelectItem>
													))}
											</SelectContent>
										</Select>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={formByStand.control}
								name="userId"
								render={({ field }) => (
									<FormItem className="w-full">
										<FormLabel className="text-muted-foreground">
											Bitte wählen Sie den Benutzer.{' '}
											<span className="text-destructive-foreground">*</span>
										</FormLabel>
										<Popover open={open} onOpenChange={setOpen}>
											<PopoverTrigger asChild>
												<FormControl>
													<Button
														variant="outline"
														role="combobox"
														className={cn(
															'hover:bg-white overflow-hidden text-ellipsis max-lg:text-xs px-4 py-[18px] h-10 lg:h-14 rounded-[12px] justify-between border-input focus-visible:border-ring focus-visible:ring-ring/50 focus:!border-3 focus:!border-secondary/50 focus-visible:ring-[3px] text-left font-normal',
															!field.value && 'text-muted-foreground',
														)}>
														<p className="max-w-11/12 overflow-hidden text-ellipsis">
															{field.value
																? `ID ${filteredUsers?.find(user => user.id === Number(field.value))?.id} ${capitalizeFirstLetter(filteredUsers?.find(user => user.id === Number(field.value))?.first_name ?? '')} ${capitalizeFirstLetter(filteredUsers?.find(user => user.id === Number(field.value))?.last_name ?? '')} (${filteredUsers?.find(user => user.id === Number(field.value))?.email})`
																: 'Benutzer auswählen'}
														</p>

														<ChevronDownIcon className="shrink-0 size-4 opacity-50" />
													</Button>
												</FormControl>
											</PopoverTrigger>
											<PopoverContent className="w-[200px] p-0 border-input">
												<Command>
													<CommandInput placeholder="Suche..." className="h-9" />
													<CommandList>
														<CommandEmpty>
															{formByStand.watch('standId')
																? 'Kein Benutzer gefunden.'
																: 'Bitte wählen Sie zuerst den Stand aus'}
														</CommandEmpty>
														<CommandGroup>
															{(formByStand.watch('standId') ? filteredUsers : [])?.map(user => (
																<CommandItem
																	value={`ID ${user.id} ${user.first_name} ${user.last_name} (
																											${user.email})`}
																	key={user.id}
																	onSelect={() => {
																		formByStand.setValue('userId', user.id.toString(), {
																			shouldValidate: true,
																		})
																		setOpen(false)
																	}}>
																	ID {user.id} {capitalizeFirstLetter(user.first_name)}{' '}
																	{capitalizeFirstLetter(user.last_name)} ({user.email})
																	<Check
																		className={cn(
																			'ml-auto',
																			user.id.toString() === field.value
																				? 'opacity-100'
																				: 'opacity-0',
																		)}
																	/>
																</CommandItem>
															))}
														</CommandGroup>
													</CommandList>
												</Command>
											</PopoverContent>
										</Popover>
										<FormMessage />
									</FormItem>
								)}
							/>
							<Button
								variant="secondary"
								className="mt-3.5 w-full lg:h-[61px] rounded-[12px] font-extrabold text-white">
								{isArchiving && <Loader2 className="animate-spin" />}
								{isArchiving ? 'Bitte warten' : 'Archivieren'}
							</Button>
						</form>
					</Form>
				</ResponsiveModalContent>
			</ResponsiveModal>
			<ResponsiveModal
				open={isUserArchiveDialogOpen}
				onOpenChange={() => {
					setIsUserArchiveDialogOpen(false)
					formByUser.reset()
					queryClient.invalidateQueries({ queryKey: ['fetch-users-for-stand'] })
				}}>
				<ResponsiveModalContent
					onPointerDownOutside={e => open && e.preventDefault()}
					className="gap-0 !overflow-visible">
					<ResponsiveModalHeader className="max-lg:pb-2">
						<ResponsiveModalTitle className="mb-0 max-lg:text-left">
							Produkte Archivieren von Benutzer
						</ResponsiveModalTitle>
						<ResponsiveModalDescription className="sr-only">
							Produkte Archivieren von Benutzer
						</ResponsiveModalDescription>
					</ResponsiveModalHeader>
					<Form {...formByUser}>
						<form
							onSubmit={formByUser.handleSubmit(onUserSubmit)}
							className="mt-4 lg:mt-8 flex flex-col gap-6 lg:gap-8">
							<FormField
								control={formByUser.control}
								name="userId"
								render={({ field }) => (
									<FormItem className="w-full">
										<FormLabel className="text-muted-foreground">
											Bitte wählen Sie den Benutzer.{' '}
											<span className="text-destructive-foreground">*</span>
										</FormLabel>
										<Popover open={open} onOpenChange={setOpen}>
											<PopoverTrigger asChild>
												<FormControl>
													<Button
														variant="outline"
														role="combobox"
														className={cn(
															'hover:bg-white overflow-hidden text-ellipsis max-lg:text-xs px-4 py-[18px] h-10 lg:h-14 rounded-[12px] justify-between border-input focus-visible:border-ring focus-visible:ring-ring/50 focus:!border-3 focus:!border-secondary/50 focus-visible:ring-[3px] text-left font-normal',
															!field.value && 'text-muted-foreground',
														)}>
														<p className="max-w-11/12 overflow-hidden text-ellipsis">
															{field.value
																? `ID ${usersList?.find(user => user.id === Number(field.value))?.id} ${capitalizeFirstLetter(usersList?.find(user => user.id === Number(field.value))?.first_name ?? '')} ${capitalizeFirstLetter(usersList?.find(user => user.id === Number(field.value))?.last_name ?? '')} (${usersList?.find(user => user.id === Number(field.value))?.email})`
																: 'Benutzer auswählen'}
														</p>

														<ChevronDownIcon className="shrink-0 size-4 opacity-50" />
													</Button>
												</FormControl>
											</PopoverTrigger>
											<PopoverContent className="w-[200px] p-0 border-input">
												<Command>
													<CommandInput placeholder="Suche..." className="h-9" />
													<CommandList>
														<CommandEmpty>Keine Benutzer gefunden</CommandEmpty>
														<CommandGroup>
															{usersList?.map(user => (
																<CommandItem
																	value={`ID ${user.id} ${user.first_name} ${user.last_name} (
																											${user.email})`}
																	key={user.id}
																	onSelect={() => {
																		formByUser.setValue('userId', user.id.toString(), {
																			shouldValidate: true,
																		})
																		fetchStands(user.id)
																		formByUser.setValue('standId', [])
																		setOpen(false)
																	}}>
																	ID {user.id} {capitalizeFirstLetter(user.first_name)}{' '}
																	{capitalizeFirstLetter(user.last_name)} ({user.email})
																	<Check
																		className={cn(
																			'ml-auto',
																			user.id.toString() === field.value
																				? 'opacity-100'
																				: 'opacity-0',
																		)}
																	/>
																</CommandItem>
															))}
														</CommandGroup>
													</CommandList>
												</Command>
											</PopoverContent>
										</Popover>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={formByUser.control}
								name="standId"
								render={({ field }) => (
									<FormItem className="w-full">
										<FormLabel className="text-muted-foreground">
											Bitte wählen Sie das Regal.{' '}
											<span className="text-destructive-foreground">*</span>
										</FormLabel>
										<FormControl>
											<MultipleSelector
												{...field}
												placeholder="Regal."
												hideClearAllButton
												badgeClassName="bg-white text-black border border-black/20"
												hidePlaceholderWhenSelected
												options={
													stands && stands.length > 0
														? stands.map(stand => ({
																label: stand.toString(),
																value: stand.toString(),
															}))
														: []
												}
												emptyIndicator={
													<p className="text-center text-xs lg:text-sm pt-1 leading-10 text-gray-600 dark:text-gray-400">
														No results found.
													</p>
												}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<Button
								variant="secondary"
								className="mt-3.5 w-full lg:h-[61px] rounded-[12px] font-extrabold text-white">
								{isArchiving && <Loader2 className="animate-spin" />}
								{isArchiving ? 'Bitte warten' : 'Archivieren'}
							</Button>
						</form>
					</Form>
				</ResponsiveModalContent>
			</ResponsiveModal>
			<ResponsiveModal
				open={isBookingsDialogOpen}
				onOpenChange={() => {
					setIsBookingsDialogOpen(false)
					formByBookings.reset()
					queryClient.invalidateQueries({ queryKey: ['fetch-users-for-stand'] })
				}}>
				<ResponsiveModalContent
					onPointerDownOutside={e => open && e.preventDefault()}
					className="gap-0 !overflow-visible">
					<ResponsiveModalHeader className="max-lg:pb-2">
						<ResponsiveModalTitle className="mb-0 max-lg:text-left">
							Produkte Archivieren nach Buchungen
						</ResponsiveModalTitle>
						<ResponsiveModalDescription className="sr-only">
							Produkte Archivieren nach Buchungen
						</ResponsiveModalDescription>
					</ResponsiveModalHeader>
					<Form {...formByBookings}>
						<form
							onSubmit={formByBookings.handleSubmit(onBookingSubmit)}
							className="mt-4 lg:mt-8 flex flex-col gap-6 lg:gap-8">
							<FormField
								control={formByBookings.control}
								name="bookingId"
								render={({ field, fieldState: { error } }) => (
									<FormItem className="w-full">
										<FormLabel className="text-muted-foreground">
											Bitte wählen Sie die Buchung aus.{' '}
											<span className="text-destructive-foreground">*</span>
										</FormLabel>
										<FormControl>
											<VirtualizedCombobox
												defaultOpen
												searchPlaceholder="Wählen Sie Buchung"
												onChange={field.onChange}
												isLoading={status === 'loading'}
												error={!!error}
												height={status === 'success' ? '250px' : '40px'}
												width="100%"
												options={
													bookings ? bookings?.data.map(booking => booking.id.toString()) : []
												}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<Button
								variant="secondary"
								className="mt-3.5 w-full lg:h-[61px] rounded-[12px] font-extrabold text-white">
								{isArchivingByBooking && <Loader2 className="animate-spin" />}
								{isArchivingByBooking ? 'Bitte warten' : 'Archivieren'}
							</Button>
						</form>
					</Form>
				</ResponsiveModalContent>
			</ResponsiveModal>
			<div className="flex max-lg:flex-col max-lg:gap-3 lg:items-center justify-between">
				<div className="flex items-center gap-2">
					<div className="lg:hidden rounded-xl border border-[#EEEFF0] size-10 flex items-center justify-center">
						<TaskSquareIcon className="text-secondary" />
					</div>
					<h1 className="text-muted-foreground lg:text-secondary text-xs lg:text-2xl font-medium lg:font-extrabold">
						Regale Liste
					</h1>
				</div>
				<div className="flex items-center max-lg:flex-col gap-2">
					<Button
						onClick={() => {
							setIsBookingsDialogOpen(true)
							formByBookings.setFocus('bookingId')
						}}
						variant="outline"
						className="text-secondary max-lg:w-full font-normal max-lg:text-xs border-secondary max-lg:h-10 rounded-lg lg:rounded-xl">
						<ArchiveIcon className="size-4 lg:size-6 shrink-0" />
						Produkte Archivieren nach Buchungen
					</Button>
					<Button
						onClick={() => {
							setIsUserArchiveDialogOpen(true)
							setOpen(true)
							formByUser.setFocus('userId')
						}}
						variant="outline"
						className="text-secondary max-lg:w-full font-normal max-lg:text-xs border-secondary max-lg:h-10 rounded-lg lg:rounded-xl">
						<ArchiveIcon className="size-4 lg:size-6 shrink-0" />
						Produkte Archivieren von Benutzer
					</Button>
					<Button
						onClick={() => {
							setIsStandArchiveDialogOpen(true)
							formByStand.setFocus('standId')
						}}
						variant="outline"
						className="text-secondary max-lg:w-full font-normal max-lg:text-xs border-secondary max-lg:h-10 rounded-lg lg:rounded-xl">
						<ArchiveIcon className="size-4 lg:size-6 shrink-0" />
						Produkte Archivieren von Stand
					</Button>
				</div>
			</div>
			<div className="flex max-lg:mt-5 mt-6 flex-col gap-3 lg:gap-4">
				<div className="flex flex-row lg:gap-4 lg:flex-col max-lg:items-center max-lg:justify-between">
					<div className="grid grid-cols-1 items-center gap-[9px] lg:hidden">
						<Select
							value={rowsPerPage.toString()}
							onValueChange={value => setRowsPerPage(Number(value))}>
							<SelectTrigger className="w-full h-10 text-xs">
								<SelectValue placeholder="zeigen 50" />
							</SelectTrigger>
							<SelectContent>
								{[50, 80, 100, 200, 500].map(pageSize => (
									<SelectItem key={pageSize} value={pageSize.toString()}>
										zeigen {pageSize}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				</div>
				<div className="lg:hidden flex flex-col gap-3">
					<Select value={selectedColumn} onValueChange={setSelectedColumn}>
						<SelectTrigger className="h-10 text-xs w-full">
							<SelectValue placeholder="Kategorie" />
						</SelectTrigger>
						<SelectContent>
							{searchableColumns.map(column => (
								<SelectItem key={column.value} value={column.value}>
									{column.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<div className="flex lg:hidden items-center gap-2">
						<Input
							value={filterValue}
							onChange={e => setFilterValue(e.target.value)}
							className="h-10 text-xs"
							placeholder="Regalnummer eingeben"
						/>
						<Button
							onClick={() => refetch()}
							variant="secondary"
							className="w-[73px] py-3 text-xs font-medium bg-secondary rounded-lg">
							Suche
						</Button>
					</div>
					<form
						onSubmit={(event: React.FormEvent<HTMLFormElement>) => {
							event.preventDefault()
							const formData = new FormData(event.currentTarget)
							const formJson = Object.fromEntries((formData as any).entries())
							const standId = formJson.stand_id

							if (standId) {
								navigate(`/stand-details/${standId}`)
							}
						}}
						className="flex items-center gap-2">
						<Input
							type="number"
							max={163}
							min={2}
							required
							name="stand_id"
							placeholder="Suchstand..."
							className="lg:h-12 w-full"
						/>
						<Button
							variant="secondary"
							className="w-[73px] py-3 text-xs font-medium bg-secondary rounded-lg">
							Suche
						</Button>
					</form>
				</div>
				<div className="flex max-lg:hidden items-center gap-3">
					<Select value={selectedColumn} onValueChange={setSelectedColumn}>
						<SelectTrigger className="flex-1">
							<SelectValue placeholder="Kategorie" />
						</SelectTrigger>
						<SelectContent>
							{searchableColumns.map(column => (
								<SelectItem key={column.value} value={column.value}>
									{column.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<Input
						value={filterValue}
						onChange={e => setFilterValue(e.target.value)}
						className="flex-1"
						placeholder="Regalnummer eingeben"
					/>
					<Button
						onClick={() => refetch()}
						variant="secondary"
						className="w-[90px] font-medium bg-secondary rounded-xl">
						Suche
					</Button>
				</div>
			</div>
			<div className="flex max-lg:hidden items-center justify-between mt-6">
				<div className="flex items-center gap-4">
					<Select
						value={rowsPerPage.toString()}
						onValueChange={value => setRowsPerPage(Number(value))}>
						<SelectTrigger className="w-[127px]">
							<SelectValue placeholder="zeigen 50" />
						</SelectTrigger>
						<SelectContent>
							{[50, 80, 100, 200, 500].map(pageSize => (
								<SelectItem key={pageSize} value={pageSize.toString()}>
									zeigen {pageSize}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
				<div className="flex items-center gap-8">
					<form
						onSubmit={(event: React.FormEvent<HTMLFormElement>) => {
							event.preventDefault()
							const formData = new FormData(event.currentTarget)
							const formJson = Object.fromEntries((formData as any).entries())
							const standId = formJson.stand_id

							if (standId) {
								navigate(`/stand-details/${standId}`)
							}
						}}
						className="flex items-center gap-2">
						<Input
							type="number"
							max={163}
							min={2}
							required
							name="stand_id"
							placeholder="Suchstand..."
							className="lg:h-12 w-[240px]"
						/>
						<Button variant="secondary" className="w-[90px] font-medium bg-secondary rounded-xl">
							Suche
						</Button>
					</form>
					<PaginationComponent
						currentPage={page}
						totalPages={data?.pagination?.totalPages ?? 1}
						onPageChange={setPage}
					/>
				</div>
			</div>
			<div className="mt-4">
				{isSmallScreen ? (
					<MobileStandsTable />
				) : (
					<DataTable columns={columns} data={sortedData ?? []} isLoading={isLoading} />
				)}
			</div>
			<div className="mt-6 lg:mt-16 flex justify-center">
				<PaginationComponent
					currentPage={page}
					totalPages={data?.pagination.totalPages ?? 1}
					onPageChange={setPage}
				/>
			</div>
		</div>
	)
}
